type PbDetails = {
    pb_type?: string | null;
    pb_rate_cdi?: number | string | null;
    pb_rate_pos_plus_cdi?: number | string | null;
    pb_rate_pos_plus_ipca?: number | string | null;
    pb_rate_pos_plus_igpm?: number | string | null;
    pb_rate_pre_annual?: number | string | null;
};

export type PrivateBondIndexAsset = {
    asset_sub_type?: string | null;
    asset_pb_details?: PbDetails | null;
};

export function parseBondRate(value?: string | number | null): number | null {
    if (value == null || value === "") {
        return null;
    }
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
    }
    const parsed = Number(String(value).replaceAll(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
}

function hasStoredRate(value?: number | string | null): boolean {
    return value != null && value !== "";
}

function formatBondRate(value: number): string {
    return String(value).replace(".", ",");
}

export function privateBondRateFromDetails(
    subType?: string | null,
    details?: PbDetails | null
): number | null {
    const indexer = (subType ?? "").trim().toUpperCase();
    if (indexer === "PRE") {
        return parseBondRate(details?.pb_rate_pre_annual);
    }
    if (indexer === "IPCA") {
        return parseBondRate(details?.pb_rate_pos_plus_ipca);
    }
    if (indexer === "IGPM" || indexer === "IGP-M") {
        return parseBondRate(details?.pb_rate_pos_plus_igpm);
    }
    if (indexer === "CDI") {
        return parseBondRate(details?.pb_rate_cdi) ?? parseBondRate(details?.pb_rate_pos_plus_cdi);
    }
    return parseBondRate(details?.pb_rate_pos_plus_ipca)
        ?? parseBondRate(details?.pb_rate_pos_plus_igpm)
        ?? parseBondRate(details?.pb_rate_cdi)
        ?? parseBondRate(details?.pb_rate_pos_plus_cdi)
        ?? parseBondRate(details?.pb_rate_pre_annual);
}

/** Índice que rende o papel — nunca CDB/LCI/LCA (`pb_type`). Contrato do app. */
export function formatPrivateBondIndexLabel(asset: PrivateBondIndexAsset): string {
    const details = asset.asset_pb_details ?? {};
    const indexer = (asset.asset_sub_type ?? "").trim().toUpperCase().replace("IGP-M", "IGPM");
    const tax = privateBondRateFromDetails(asset.asset_sub_type, details);
    if (indexer === "PRE" && tax != null) {
        return `${formatBondRate(tax)}% a.a.`;
    }
    if ((indexer === "IPCA" || indexer === "IGPM") && tax != null) {
        return `${indexer} + ${formatBondRate(tax)}%`;
    }
    if (indexer === "CDI" && tax != null) {
        return `${formatBondRate(tax)}% do CDI`;
    }
    if (indexer === "IPCA" || indexer === "IGPM" || indexer === "CDI" || indexer === "PRE") {
        return indexer;
    }
    if (hasStoredRate(details.pb_rate_pos_plus_ipca)) {
        return formatPrivateBondIndexLabel({ asset_sub_type: "IPCA", asset_pb_details: details });
    }
    if (hasStoredRate(details.pb_rate_pos_plus_igpm)) {
        return formatPrivateBondIndexLabel({ asset_sub_type: "IGPM", asset_pb_details: details });
    }
    if (hasStoredRate(details.pb_rate_cdi) || hasStoredRate(details.pb_rate_pos_plus_cdi)) {
        return formatPrivateBondIndexLabel({ asset_sub_type: "CDI", asset_pb_details: details });
    }
    if (hasStoredRate(details.pb_rate_pre_annual)) {
        return formatPrivateBondIndexLabel({ asset_sub_type: "PRE", asset_pb_details: details });
    }
    return indexer || "—";
}

export function shortInstitutionName(name?: string | null): string {
    if (!name?.trim()) {
        return "";
    }
    const shortened = name
        .replace(/\b(s\.?a\.?|s\/a|ltda\.?)\b/gi, " ")
        .replace(/^(banco\s+cooperativo\s+|cooperativa\s+de\s+cr[eé]dito\s+|banco\s+)/i, "")
        .replace(/\s+/g, " ")
        .trim();
    return shortened || name.trim();
}

export function formatPrivateBondCardSubtitle(
    indexLabel?: string | null,
    bankName?: string | null
): string {
    const index = indexLabel && indexLabel !== "—" ? indexLabel : "";
    const bank = shortInstitutionName(bankName);
    return [index, bank].filter(Boolean).join(" · ");
}

export function formatPrivateBondAnalysisName(
    pbType?: string | null,
    asset?: PrivateBondIndexAsset
): string {
    const type = (pbType ?? "").trim() || "CDB";
    const index = formatPrivateBondIndexLabel(asset ?? {});
    return index && index !== "—" ? `${type} · ${index}` : type;
}
